// Password Strength Meter Component

import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { PasswordStrengthResult } from '../types/registration';
import {
  getPasswordStrengthColor,
  getPasswordStrengthLabel
} from '../utils/passwordValidation';

interface PasswordStrengthMeterProps {
  password: string;
  strengthResult: PasswordStrengthResult;
  showRequirements?: boolean;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  strengthResult,
  showRequirements = true
}) => {
  const color = getPasswordStrengthColor(strengthResult.strength);
  const label = getPasswordStrengthLabel(strengthResult.strength);

  return (
    <Box sx={{ mt: 1 }}>
      {/* Progress Bar */}
      {password && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={strengthResult.score}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: color,
                    borderRadius: 4
                  }
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: color,
                fontWeight: 600,
                minWidth: 80,
                textAlign: 'right'
              }}
            >
              {label}
            </Typography>
          </Box>

          {/* Feedback Messages */}
          {strengthResult.feedback.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {strengthResult.feedback.slice(0, 3).map((feedback, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  <InfoIcon sx={{ fontSize: 14 }} />
                  {feedback}
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Requirements Checklist */}
      {showRequirements && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mt: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Password Requirements:
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {strengthResult.requirementsMet.minLength ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="At least 8 characters"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: strengthResult.requirementsMet.minLength ? 'success.main' : 'text.secondary'
                }}
              />
            </ListItem>

            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {strengthResult.requirementsMet.hasUppercase ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="One uppercase letter"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: strengthResult.requirementsMet.hasUppercase ? 'success.main' : 'text.secondary'
                }}
              />
            </ListItem>

            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {strengthResult.requirementsMet.hasLowercase ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="One lowercase letter"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: strengthResult.requirementsMet.hasLowercase ? 'success.main' : 'text.secondary'
                }}
              />
            </ListItem>

            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {strengthResult.requirementsMet.hasNumbers ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="One number"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: strengthResult.requirementsMet.hasNumbers ? 'success.main' : 'text.secondary'
                }}
              />
            </ListItem>

            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {strengthResult.requirementsMet.hasSpecialChars ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="One special character (!@#$%^&*...)"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: strengthResult.requirementsMet.hasSpecialChars ? 'success.main' : 'text.secondary'
                }}
              />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default PasswordStrengthMeter;
